import { AxiosError } from 'axios';

export enum EApiErrorSource {
    Auth = 'auth',
    Cliniko = 'cliniko',
    Middleware = 'middleware',
    Network = 'network',
    Validation = 'validation'
}

export type TApiErrorInternalIds = Partial<{
    patient_id: string;
    clinic_id: string;
    appointment_id: string;
    file_id: string;
    case_id: string;
}>;

export type TApiErrorResponse = TApiErrorInternalIds & {
    error: string;
    title?: string;
    status?: number;
    statusCode?: number;
    code?: string;
    reference?: string;
    apiEndpoint: string;
    apiErrorSource: EApiErrorSource;
    apiMethod: string;
    apiStatus?: number;
    apiCode?: string;
    apiReference?: string;
    isApiError: true;
    isAuthError: boolean;
    isNetworkError: boolean;
    isTimeout: boolean;
};

export const isApiErrorResponse = (value: unknown): value is TApiErrorResponse => {
    return (
        !!value &&
        typeof value === 'object' &&
        (value as { isApiError?: unknown }).isApiError === true
    );
};

const ID_SEGMENT_PATTERN = /^\d+$/;
const EMAIL_SEGMENT_PATTERN = /^[^\s/@]+@[^\s/@]+\.[^\s/@]+$/;
const UUID_SEGMENT_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const EXTERNAL_ID_SEGMENT_PATTERN =
    /^(?:pm|pi|cus|sub|price|prod|cs|ch|seti|si|inv|in|txn|evt)_[A-Za-z0-9_]+$/;
const HASH_SEGMENT_PATTERN = /^[a-f0-9]{16,}$/i;

const isNumericId = (value: string | undefined): value is string =>
    !!value && ID_SEGMENT_PATTERN.test(value);

const withoutQueryString = (endpoint: string): string => endpoint.split('?')[0];

export const getApiEndpointTemplate = (endpoint: string): string => {
    return withoutQueryString(endpoint)
        .split('/')
        .filter(Boolean)
        .map((segment) => {
            if (ID_SEGMENT_PATTERN.test(segment)) return ':id';
            if (UUID_SEGMENT_PATTERN.test(segment)) return ':id';
            if (EXTERNAL_ID_SEGMENT_PATTERN.test(segment)) return ':id';
            if (HASH_SEGMENT_PATTERN.test(segment)) return ':id';
            if (EMAIL_SEGMENT_PATTERN.test(segment)) return ':value';
            if (segment.length > 80) return ':value';
            return segment;
        })
        .join('/');
};

export const extractApiInternalIds = (endpoint: string): TApiErrorInternalIds => {
    const segments = withoutQueryString(endpoint).split('/').filter(Boolean);
    const ids: TApiErrorInternalIds = {};

    segments.forEach((segment, index) => {
        const next = segments[index + 1];
        const nextAfterNext = segments[index + 2];

        if (segment === 'clinic' && isNumericId(next)) {
            ids.clinic_id = next;
        }

        if (segment === 'restricted_dates' && isNumericId(next)) {
            ids.clinic_id = next;
        }

        if (segment === 'reception' && isNumericId(next)) {
            ids.clinic_id = next;
        }

        // Recall case ids are their own namespace — never fold them into patient_id.
        if (segment === 'recall_cases' && isNumericId(next)) {
            ids.case_id = next;
        }

        if (segment === 'patient_profile' && isNumericId(next)) {
            ids.patient_id = next;
        }

        if (segment === 'patient' && isNumericId(next) && segments[index + 2] !== 'appointments') {
            ids.patient_id = next;
        }

        if (
            ['appointments', 'appointment', 'appointment_state'].includes(segment) &&
            isNumericId(next)
        ) {
            ids.appointment_id = next;
        }

        if (segment === 'patient_files' && isNumericId(next)) {
            ids.patient_id = next;
            if (isNumericId(nextAfterNext)) {
                ids.file_id = nextAfterNext;
            }
        }

        if (
            [
                'aftercare_template',
                'balance_programme',
                'balance_clinician_notes',
                'clinician_notes',
                'data_sharing',
                'loyalty',
                'medical_form',
                'skin_grades',
                'treatment_consent',
                'treatmentplan'
            ].includes(segment) &&
            isNumericId(next)
        ) {
            ids.patient_id = next;
        }
    });

    const receptionPatientMatch = withoutQueryString(endpoint).match(
        /^reception\/patient\/(\d+)\/(\d+)/
    );
    if (receptionPatientMatch) {
        ids.clinic_id = receptionPatientMatch[1];
        ids.patient_id = receptionPatientMatch[2];
    }

    const patientAppointmentMatch = withoutQueryString(endpoint).match(
        /^patient\/(\d+)\/appointments\/(\d+)/
    );
    if (patientAppointmentMatch) {
        ids.clinic_id = patientAppointmentMatch[1];
        ids.appointment_id = patientAppointmentMatch[2];
    }

    const invoicePatientMatch = withoutQueryString(endpoint).match(
        /^invoice\/patient\/(\d+)\/(\d+)/
    );
    if (invoicePatientMatch) {
        ids.clinic_id = invoicePatientMatch[1];
        ids.patient_id = invoicePatientMatch[2];
    }

    const invoiceAppointmentMatch = withoutQueryString(endpoint).match(
        /^invoice\/appointment\/(\d+)\/(\d+)/
    );
    if (invoiceAppointmentMatch) {
        ids.clinic_id = invoiceAppointmentMatch[1];
        ids.appointment_id = invoiceAppointmentMatch[2];
    }

    return ids;
};

const getApiErrorSource = ({
    endpoint,
    isAuthError,
    isNetworkError,
    message,
    status
}: {
    endpoint: string;
    isAuthError: boolean;
    isNetworkError: boolean;
    message?: string;
    status?: number;
}): EApiErrorSource => {
    if (isAuthError) return EApiErrorSource.Auth;
    if (isNetworkError) return EApiErrorSource.Network;
    if (status && status >= 400 && status < 500) return EApiErrorSource.Validation;
    if (endpoint.startsWith('cliniko/') || /cliniko/i.test(message ?? '')) {
        return EApiErrorSource.Cliniko;
    }
    return EApiErrorSource.Middleware;
};

export const normalizeApiError = (
    err: AxiosError | Error | unknown,
    endpoint: string,
    method: string
): TApiErrorResponse => {
    const axiosError = err as AxiosError;
    const rawResponseData = axiosError.response?.data;
    const responseData =
        rawResponseData && typeof rawResponseData === 'object' && !Array.isArray(rawResponseData)
            ? (rawResponseData as Record<string, unknown>)
            : {};
    const status =
        axiosError.response?.status ??
        (typeof responseData.status === 'number' ? responseData.status : undefined);
    const code = axiosError.code;
    // A machine-readable code in the response body (the recall workflow's 422 `code`,
    // for example) is what a caller wants to branch on; the axios transport code stays
    // available as `apiCode`, which is also what error tracking reads.
    const bodyCode = typeof responseData.code === 'string' ? responseData.code : undefined;
    const isNetworkError = !!axiosError.isAxiosError && !axiosError.response;
    const isTimeout =
        code === AxiosError.ECONNABORTED ||
        code === AxiosError.ETIMEDOUT ||
        /timeout/i.test(axiosError.message ?? '');
    const isAuthError = status === 401 || status === 403;
    const apiEndpoint = getApiEndpointTemplate(endpoint);
    const apiReference =
        typeof responseData.reference === 'string' ? responseData.reference : undefined;
    const title =
        typeof responseData.title === 'string'
            ? responseData.title
            : typeof responseData.error === 'string'
            ? responseData.error
            : undefined;
    const message =
        typeof responseData.error === 'string'
            ? responseData.error
            : title ?? axiosError.message ?? 'API request failed';

    return {
        ...responseData,
        ...extractApiInternalIds(endpoint),
        error: message,
        title,
        status,
        statusCode: status,
        code: bodyCode ?? code,
        reference: apiReference,
        apiEndpoint,
        apiErrorSource: getApiErrorSource({
            endpoint,
            isAuthError,
            isNetworkError,
            message,
            status
        }),
        apiMethod: method,
        apiStatus: status,
        apiCode: code,
        apiReference,
        isApiError: true,
        isAuthError,
        isNetworkError,
        isTimeout
    };
};

export const getApiErrorTrackingProperties = (
    data: Partial<TApiErrorResponse>
): Record<string, unknown> => ({
    api_error_source: data.apiErrorSource,
    api_method: data.apiMethod,
    api_endpoint: data.apiEndpoint,
    api_status: data.apiStatus ?? data.status ?? data.statusCode,
    api_code: data.apiCode ?? data.code,
    api_reference: data.apiReference ?? data.reference,
    api_backend_title: data.title,
    patient_id: data.patient_id,
    clinic_id: data.clinic_id,
    appointment_id: data.appointment_id,
    file_id: data.file_id,
    case_id: data.case_id
});
